import { startCase } from 'lodash-es';
import { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VButton, VCheckbox, VFlex, VInput, VStack, VTable } from '@/package';
import type { Task } from '@/type';

import {
  useDistributeEvenly,
  useRedistributionErrorMessage,
  useRenderedFrameList,
  useWorkRedistributeForm,
  useWorkRedistributeSubmit,
} from './hook';
import type { WorkRedistributeFormData } from './type';

type MemberRedistributionTableProps = {
  renderedFrameList: ReturnType<
    typeof useRenderedFrameList
  >['renderedFrameList'];
  task: Task;
};

const MemberRedistributionTable = ({
  renderedFrameList,
  task,
}: MemberRedistributionTableProps) => {
  const { t } = useTranslation();
  const { watch, setValue, trigger } =
    useFormContext<WorkRedistributeFormData>();
  const unAssignedUserIDList = watch('unAssignedUserID');

  return (
    <VStack className="flex-1 overflow-hidden" gap={0.5}>
      <ul className="list-inside! list-disc! pt-2 text-sm">
        <li>{t('task.distributeDescription0.browning')}</li>
        <li>{t('task.distributeDescription1.volumette')}</li>
        <li>{t('task.distributeDescription2.scalene')}</li>
      </ul>

      <div className="flex-1 overflow-y-auto">
        <VTable
          className="overflow-x-hidden"
          data={renderedFrameList}
          columnList={[
            'name',
            'inProgressFrame',
            'completedFrame',
            'startFrame',
            'endFrame',
            'unassigned',
          ]}
          columnStyleMap={{
            name: { className: 'flex items-center text-body-sm w-[143px]' },
            inProgressFrame: {
              className: 'flex items-center text-body-sm w-[100px]',
            },
            completedFrame: {
              className: 'flex items-center text-body-sm w-[100px]',
            },
            startFrame: {
              className: 'flex items-center text-body-sm w-[114px]',
            },
            endFrame: { className: 'flex items-center text-body-sm w-[114px]' },
            unassigned: {
              className: 'flex items-center text-body-sm w-[69px]',
            },
          }}
          cellRenderer={{
            name: value => <div className="text-body-md">{value}</div>,
            inProgressFrame: (value, _, row) => {
              return (
                <VInput key={row.id} size="sm" readOnly value={value} fluid />
              );
            },
            completedFrame: (value, _, row) => {
              return (
                <VInput key={row.id} size="sm" readOnly value={value} fluid />
              );
            },
            startFrame: (_1, _2, row) => {
              return (
                <VInput
                  key={row.id}
                  size="sm"
                  fluid
                  disabled={row.unassigned}
                  {...row.registerStartFrame}
                  valid={row.isValidStartFrame}
                />
              );
            },
            endFrame: (_1, _2, row) => {
              return (
                <VInput
                  key={row.id}
                  size="sm"
                  fluid
                  disabled={row.unassigned}
                  {...row.registerEndFrame}
                  valid={row.isValidEndFrame}
                />
              );
            },
            unassigned: (value, _, row) => (
              <VFlex items="center" justify="center">
                <VCheckbox
                  checked={value}
                  onCheckedChange={async checked => {
                    if (checked) {
                      setValue(
                        'unAssignedUserID',
                        [...unAssignedUserIDList, row.id.toString()],
                        { shouldValidate: true },
                      );
                    } else {
                      setValue(
                        'unAssignedUserID',
                        unAssignedUserIDList.filter(
                          id => id !== row.id.toString(),
                        ),
                        { shouldValidate: true },
                      );
                    }

                    await trigger([
                      `taskAllocations.${row.index}.allocateRange.0`,
                      `taskAllocations.${row.index}.allocateRange.1`,
                    ]);
                  }}
                />
              </VFlex>
            ),
          }}
          columnRenderer={{
            name: value => (
              <div className="text-body-sm">{startCase(value)}</div>
            ),
            completedFrame: value => (
              <div className="text-body-sm">{startCase(value)}</div>
            ),
            startFrame: value => (
              <div className="text-body-sm">{startCase(value)}</div>
            ),
            endFrame: value => (
              <div className="text-body-sm">{startCase(value)}</div>
            ),
            unassigned: value => (
              <div className="text-body-sm">{startCase(value)}</div>
            ),
          }}
        />
      </div>

      <VFlex className="px-2 py-1" items="center">
        <div className="flex-1 text-body-md">Total</div>
        <div className="w-[100px] px-2">
          <VInput
            size="sm"
            fluid
            value={task.userTask?.reduce(
              (acc, curr) =>
                acc + curr.inProgressFrameCount + curr.rejectedFrameCount,
              0,
            )}
            readOnly
          />
        </div>
        <div className="w-[100px] px-2">
          <VInput
            size="sm"
            fluid
            value={task.userTask?.reduce(
              (acc, curr) =>
                acc +
                curr.submittedFrameCount +
                curr.inReviewFrameCount +
                curr.completedFrameCount,
              0,
            )}
            readOnly
          />
        </div>
        <div className="w-[114px] px-2">
          <VInput size="sm" fluid value={task.startFrameSeq || 1} readOnly />
        </div>
        <div className="w-[114px] px-2">
          <VInput
            size="sm"
            fluid
            value={task.endFrameSeq || task.fullFrameCount}
            readOnly
          />
        </div>
        <div className="w-[69px]" />
      </VFlex>
    </VStack>
  );
};

type MemberRedistributionTableFormProps = {
  distributeEvenlyTimestamp?: number;
  task: Task;
  onCancel?(): void;
};

export const MemberRedistributionTableForm = ({
  distributeEvenlyTimestamp,
  task,
  onCancel,
}: MemberRedistributionTableFormProps) => {
  const { t } = useTranslation();
  const { formState, watch, setValue, trigger } =
    useFormContext<WorkRedistributeFormData>();
  const unAssignedUserIDList = watch('unAssignedUserID');
  const taskAllocations = watch('taskAllocations');
  const { isValid } = formState;
  const { distributeEvenly } = useDistributeEvenly(task);
  const { renderedFrameList } = useRenderedFrameList(task);
  const { errorMessage } = useRedistributionErrorMessage(
    task,
    renderedFrameList,
  );
  const { onSubmit } = useWorkRedistributeSubmit(
    task,
    unAssignedUserIDList,
    () => onCancel?.(),
  );

  useEffect(() => {
    if (!distributeEvenlyTimestamp || distributeEvenlyTimestamp <= 0) return;

    setValue('taskAllocations', distributeEvenly(), {
      shouldValidate: true,
    });

    taskAllocations.map((_, index) =>
      trigger([
        `taskAllocations.${index}.allocateRange.0`,
        `taskAllocations.${index}.allocateRange.1`,
      ]),
    );
  }, [distributeEvenlyTimestamp]);

  return (
    <form
      className="flex flex-1 flex-col gap-2 overflow-hidden"
      onSubmit={onSubmit}
    >
      <MemberRedistributionTable
        renderedFrameList={renderedFrameList}
        task={task}
      />

      <VStack>
        <VFlex items="center" justify="between" className="pt-2 pb-4">
          <div className="text-body-sm text-error-500">{errorMessage}</div>
          <VFlex items="center" gap={0.5}>
            <VButton
              outlined
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => onCancel?.()}
            >
              {t('task.distributeCancel.drumskin')}
            </VButton>
            <VButton
              type="submit"
              size="sm"
              disabled={!isValid || !!errorMessage}
            >
              {t('task.distributeComplete.squeeze')}
            </VButton>
          </VFlex>
        </VFlex>
      </VStack>
    </form>
  );
};

export const MemberRedistributionTableFormWrapper = (
  props: MemberRedistributionTableFormProps,
) => {
  const distributionForm = useWorkRedistributeForm(props.task);
  return (
    <FormProvider {...distributionForm}>
      <MemberRedistributionTableForm {...props} />
    </FormProvider>
  );
};

MemberRedistributionTable.displayName = 'MemberRedistributionTable';
MemberRedistributionTableForm.displayName = 'MemberRedistributionTableForm';
MemberRedistributionTableFormWrapper.displayName =
  'MemberRedistributionTableFormWrapper';
